import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BellSimple, SpeakerHigh, DeviceMobile, TelegramLogo, Upload, Copy, CheckCircle,
} from "@phosphor-icons/react";
import AppShell from "@/components/AppShell";
import { api, fmtApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  pushSupported, getPermissionState, subscribePush, unsubscribePush, sendPushTest, registerServiceWorker,
} from "@/lib/push";
import { invalidateAlertSoundCache, playAlertSound } from "@/lib/alertSound";

const ALL_EVENTS = ["CREATED", "ENTERED", "TP1_HIT", "TP2_HIT", "SL_HIT", "EXPIRED", "NEWS_HIGH_RISK"];
const PAIRS = ["XAUUSD"];

function Toggle({ checked, onChange, label, sub, testId }) {
  return (
    <label className="flex items-start gap-4 py-4 border-b border-white/5 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testId}
        onClick={() => onChange(!checked)}
        className={`mt-1 w-10 h-5 flex items-center px-0.5 transition shrink-0 rounded-full ${
          checked ? "bg-gold" : "bg-zinc-700"
        }`}
      >
        <span className={`w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
      </div>
    </label>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="surface p-6 mb-5">
      <div className="flex items-start gap-3 mb-4">
        <Icon size={20} className="text-gold mt-0.5" />
        <div>
          <div className="heading text-lg font-semibold tracking-tight">{title}</div>
          {subtitle && <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === "admin";

  const { data: prefsData } = useQuery({
    queryKey: ["prefs"],
    queryFn: () => api.get("/prefs").then((r) => r.data),
  });
  const prefs = prefsData?.prefs || {
    in_app: true, sound: true, push: false, telegram: false, events: ALL_EVENTS, symbols: [],
  };

  const saveMut = useMutation({
    mutationFn: (body) => api.put("/prefs", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prefs"] }),
  });
  const update = (patch) => saveMut.mutate({ ...prefs, ...patch });

  // ── Web push ────────────────────────────────────────────────────────────
  const [pushPerm, setPushPerm] = useState("default");
  useEffect(() => { registerServiceWorker(); getPermissionState().then(setPushPerm); }, []);

  const enablePush = async () => {
    try {
      await subscribePush();
      setPushPerm("granted");
      update({ push: true });
      toast({ title: "Web Push enabled" });
    } catch (e) {
      toast({ title: "Web Push failed", description: e.message, variant: "destructive" });
    }
  };
  const disablePush = async () => {
    try { await unsubscribePush(); update({ push: false }); toast({ title: "Web Push disabled" }); }
    catch (e) { toast({ title: "Could not unsubscribe", description: e.message, variant: "destructive" }); }
  };

  // ── Telegram ────────────────────────────────────────────────────────────
  const { data: tgStatus } = useQuery({
    queryKey: ["telegram-status"],
    queryFn: () => api.get("/telegram/status").then((r) => r.data),
    refetchInterval: 5000,
  });
  const [link, setLink] = useState(null);
  const generateLink = async () => {
    try {
      const r = await api.post("/telegram/link");
      setLink(r.data);
    } catch (e) {
      toast({
        title: "Telegram not configured",
        description: fmtApiError(e?.response?.data?.detail),
        variant: "destructive",
      });
    }
  };
  const unlinkTg = async () => {
    await api.post("/telegram/unlink");
    qc.invalidateQueries({ queryKey: ["telegram-status"] });
    setLink(null);
  };

  // ── Admin: telegram token / sound upload ────────────────────────────────
  const { data: adminTg } = useQuery({
    queryKey: ["admin-telegram"],
    queryFn: () => api.get("/settings/admin/telegram").then((r) => r.data),
    enabled: isAdmin,
  });
  const [tokenInput, setTokenInput] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const saveAdminTg = async (e) => {
    e.preventDefault();
    try {
      await api.post("/settings/admin/telegram", { token: tokenInput.trim(), username: usernameInput.trim() || null });
      setTokenInput(""); setUsernameInput("");
      qc.invalidateQueries({ queryKey: ["admin-telegram"] });
      qc.invalidateQueries({ queryKey: ["telegram-status"] });
      toast({ title: "Telegram bot token saved" });
    } catch (e2) {
      toast({ title: "Failed to save", description: fmtApiError(e2?.response?.data?.detail), variant: "destructive" });
    }
  };
  const clearAdminTg = async () => {
    await api.delete("/settings/admin/telegram");
    qc.invalidateQueries({ queryKey: ["admin-telegram"] });
    qc.invalidateQueries({ queryKey: ["telegram-status"] });
  };

  // sound upload
  const onSoundUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/sound", fd, { headers: { "Content-Type": "multipart/form-data" } });
      invalidateAlertSoundCache();
      toast({ title: "Alert sound updated" });
    } catch (e) {
      toast({ title: "Upload failed", description: fmtApiError(e?.response?.data?.detail), variant: "destructive" });
    }
  };

  const toggleEvent = (ev) => {
    const set = new Set(prefs.events || []);
    set.has(ev) ? set.delete(ev) : set.add(ev);
    update({ events: Array.from(set) });
  };
  const toggleSymbol = (sym) => {
    const set = new Set(prefs.symbols || []);
    set.has(sym) ? set.delete(sym) : set.add(sym);
    update({ symbols: Array.from(set) });
  };

  return (
    <AppShell>
      <div className="p-6 md:p-10 max-w-3xl" data-testid="settings-page">
        <div className="overline mb-3">Preferences</div>
        <h1 className="heading text-3xl md:text-5xl font-bold tracking-tighter mb-8">
          Settings. <span className="text-gold-grad">Your alerts, your way.</span>
        </h1>

        <Section icon={BellSimple} title="Master switches" subtitle="Where alerts get delivered.">
          <Toggle testId="pref-in-app"   checked={!!prefs.in_app}   onChange={(v) => update({ in_app: v })}   label="In-app toast" sub="Visible toast in the corner of the dashboard" />
          <Toggle testId="pref-sound"    checked={!!prefs.sound}    onChange={(v) => update({ sound: v })}    label="Sound" sub="Plays the alert chime on every notification" />
          <div className="py-4 border-b border-white/5 flex items-center gap-4">
            <button data-testid="settings-test-sound" onClick={() => playAlertSound()} className="mono text-[11px] uppercase tracking-widest px-3 py-1.5 border border-white/10 hover:bg-white/5">Test sound</button>
          </div>
          <Toggle testId="pref-push"
            checked={!!prefs.push}
            onChange={(v) => (v ? enablePush() : disablePush())}
            label="Web push (browser)"
            sub={
              !pushSupported() ? "Not supported in this browser"
              : pushPerm === "denied" ? "Permission denied — re-enable in browser settings"
              : "Native browser notifications even when the tab is closed"
            }
          />
          <Toggle testId="pref-telegram"
            checked={!!prefs.telegram}
            onChange={(v) => update({ telegram: v })}
            label="Telegram"
            sub={tgStatus?.linked ? "Linked · alerts will arrive on Telegram" : tgStatus?.bot_configured ? "Bot configured — link below" : "Bot not configured by admin yet"}
          />
        </Section>

        <Section icon={BellSimple} title="Which events to alert on">
          <div className="flex flex-wrap gap-2">
            {ALL_EVENTS.map((e) => (
              <button
                key={e}
                type="button"
                data-testid={`pref-event-${e.toLowerCase()}`}
                onClick={() => toggleEvent(e)}
                className={`px-3 py-1.5 mono text-[11px] uppercase tracking-widest border transition ${
                  prefs.events?.includes(e) ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                {e.replace("_", " ")}
              </button>
            ))}
          </div>
        </Section>

        <Section icon={BellSimple} title="Symbols to follow" subtitle="Leave all unselected to receive alerts for every pair.">
          <div className="flex flex-wrap gap-2">
            {PAIRS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleSymbol(s)}
                className={`px-3 py-1.5 mono text-[11px] uppercase tracking-widest border transition ${
                  prefs.symbols?.includes(s) ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </Section>

        <Section icon={TelegramLogo} title="Telegram link" subtitle={tgStatus?.bot_configured ? `Bot: @${tgStatus.bot_username || "your bot"}` : "Bot not configured by admin yet."}>
          {tgStatus?.linked ? (
            <div className="flex items-center gap-3">
              <CheckCircle size={20} className="text-bull" />
              <span className="text-sm">Linked. Alerts will arrive on Telegram.</span>
              <button type="button" onClick={unlinkTg} className="ml-auto mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white">Unlink</button>
            </div>
          ) : (
            <div className="space-y-3">
              <button
                type="button"
                onClick={generateLink}
                disabled={!tgStatus?.bot_configured}
                data-testid="telegram-generate-link"
                className="px-4 py-2 mono text-[11px] uppercase tracking-widest bg-gold text-black hover:bg-yellow-400 disabled:opacity-40 disabled:hover:bg-gold"
              >
                Generate link
              </button>
              {link && (
                <div className="surface p-4 bg-black/40">
                  <div className="overline mb-2">Open this in Telegram (valid {link.ttl_minutes}m)</div>
                  <div className="flex items-center gap-3 break-all">
                    <a href={link.deep_link} target="_blank" rel="noreferrer" className="mono text-xs text-gold underline">{link.deep_link}</a>
                    <button onClick={() => navigator.clipboard.writeText(link.deep_link)} className="text-zinc-400 hover:text-white"><Copy size={14} /></button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Admin-only blocks */}
        {isAdmin && (
          <>
            <Section icon={TelegramLogo} title="Admin · Telegram bot token" subtitle="Paste your @BotFather token. Stored encrypted in the system_settings collection.">
              {adminTg?.configured && (
                <div className="text-xs text-zinc-400 mb-3">
                  Configured · last 6 chars <span className="mono text-white">{adminTg.tail}</span>
                  {adminTg.username && <> · @{adminTg.username}</>}
                </div>
              )}
              <form onSubmit={saveAdminTg} className="space-y-3">
                <input type="text" placeholder="Bot token (e.g. 1234567:AB...)" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 px-3 py-2 mono text-xs focus:border-gold/60 focus:outline-none" data-testid="admin-tg-token" />
                <input type="text" placeholder="Bot username (without @)" value={usernameInput} onChange={(e) => setUsernameInput(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 px-3 py-2 mono text-xs focus:border-gold/60 focus:outline-none" data-testid="admin-tg-username" />
                <div className="flex gap-2">
                  <button type="submit" data-testid="admin-tg-save" className="px-4 py-2 mono text-[11px] uppercase tracking-widest bg-gold text-black hover:bg-yellow-400">Save</button>
                  {adminTg?.configured && (
                    <button type="button" data-testid="admin-tg-clear" onClick={clearAdminTg} className="px-4 py-2 mono text-[11px] uppercase tracking-widest border border-white/10 hover:bg-white/5">Clear</button>
                  )}
                </div>
              </form>
            </Section>

            <Section icon={Upload} title="Admin · Alert sound" subtitle="Upload a 1-2s audio file (max 500 KB). Used as the chime on every alert.">
              <label className="inline-flex items-center gap-2 px-4 py-2 mono text-[11px] uppercase tracking-widest border border-white/10 hover:bg-white/5 cursor-pointer">
                <Upload size={14} /> Choose file
                <input type="file" accept="audio/*" className="hidden" onChange={(e) => onSoundUpload(e.target.files?.[0])} data-testid="admin-sound-upload" />
              </label>
            </Section>
          </>
        )}
      </div>
    </AppShell>
  );
}
